import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const TermsOfService = () => {
  const navigate = useNavigate();

  // Ensure body can scroll - override any App.css restrictions
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    document.body.style.overflow = "auto";
    document.body.style.height = "auto";
    document.documentElement.style.overflow = "auto";
    document.documentElement.style.height = "auto";

    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
    };
  }, []);

  return (
    <div
      className="bg-gray-50 dark:bg-gray-900"
      style={{ minHeight: "100vh", overflow: "auto" }}
    >
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate("/")}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <svg
                className="h-5 w-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Home
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              WealthPort AI
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Last Updated: February 26, 2026
          </p>

          <div className="prose prose-lg dark:prose-invert max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Welcome to WealthPort. These Terms of Service ("Terms") govern
                your access to and use of WealthPort's portfolio tracking and
                trading intelligence platform (the "Service"). By accessing or
                using the Service, you agree to be bound by these Terms.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>
                  IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE THE SERVICE.
                </strong>
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                WealthPort reserves the right to modify these Terms at any time.
                Continued use of the Service after changes constitutes
                acceptance of the modified Terms.
              </p>
            </section>

            {/* Financial Disclaimer */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                2. Financial Disclaimer
              </h2>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                  IMPORTANT: NOT FINANCIAL ADVICE
                </p>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  WealthPort is a portfolio tracking tool only. We do NOT
                  provide investment advice, recommendations, or financial
                  planning services.
                </p>
              </div>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  <strong>Not a Registered Advisor:</strong> WealthPort is not a
                  registered investment advisor, broker-dealer, or financial
                  institution. We are not licensed to provide financial advice.
                </li>
                <li>
                  <strong>No Investment Recommendations:</strong> Information
                  provided through the Service is for informational and tracking
                  purposes only and should not be construed as investment advice
                  or recommendations.
                </li>
                <li>
                  <strong>Trading Risks:</strong> Trading securities involves
                  substantial risk of loss. Past performance does not guarantee
                  future results. You may lose some or all of your invested
                  capital.
                </li>
                <li>
                  <strong>Your Responsibility:</strong> All investment decisions
                  are your sole responsibility. You should consult with a
                  licensed financial advisor before making any investment
                  decisions.
                </li>
                <li>
                  <strong>No Fiduciary Duty:</strong> WealthPort has no
                  fiduciary duty to you and is not acting as your advisor or
                  agent.
                </li>
              </ul>
            </section>

            {/* Data Accuracy */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                3. Data Accuracy and Service Availability
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                While we strive for accuracy, WealthPort makes no guarantees:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  <strong>Market Data:</strong> Stock prices and market data may
                  be delayed, inaccurate, or incomplete. We rely on third-party
                  data providers and are not responsible for their errors.
                </li>
                <li>
                  <strong>Calculations:</strong> Performance metrics, P&L
                  calculations, and analytics are estimates. You are responsible
                  for verifying all calculations independently.
                </li>
                <li>
                  <strong>Service Availability:</strong> We do not guarantee
                  uninterrupted or error-free service. The Service may be
                  unavailable due to maintenance, technical issues, or other
                  factors beyond our control.
                </li>
                <li>
                  <strong>User Data:</strong> You are solely responsible for the
                  accuracy of portfolio data, transactions, and other
                  information you enter into the Service.
                </li>
                <li>
                  <strong>Third-Party Integrations:</strong> We rely on
                  third-party services including SnapTrade and Plaid Inc. (for
                  brokerage connections) and Yahoo Finance/yfinance (for market
                  data). These services may fail, provide inaccurate data, or
                  become unavailable. We are not responsible for third-party
                  service failures, data breaches, or terms changes.
                </li>
                <li>
                  <strong>Read-Only Brokerage Access:</strong> WealthPort
                  accesses your connected brokerage accounts in read-only mode
                  for the purpose of importing holdings, balances, and
                  transaction history. WealthPort does not place, modify, or
                  cancel any orders or trades on your behalf.
                </li>
                <li>
                  <strong>Third-Party Terms:</strong> When you connect a
                  brokerage account through SnapTrade or Plaid, you are also
                  subject to their respective terms and privacy policies. When
                  we retrieve market data via Yahoo Finance, you acknowledge
                  that Yahoo's terms may apply.
                </li>
              </ul>
            </section>

            {/* User Accounts */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                4. User Accounts and Responsibilities
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.1 Account Security
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>
                  You are responsible for maintaining the confidentiality of
                  your account and email access.
                </li>
                <li>
                  You agree to immediately notify us of any unauthorized access
                  to your account.
                </li>
                <li>
                  You are responsible for all activities that occur under your
                  account.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.2 User Conduct
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You agree NOT to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  Use the Service for any illegal purpose or in violation of any
                  laws
                </li>
                <li>
                  Attempt to gain unauthorized access to any part of the Service
                </li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>
                  Use automated tools to scrape, copy, or harvest data from the
                  Service
                </li>
                <li>
                  Reverse engineer, decompile, or disassemble any part of the
                  Service
                </li>
                <li>Upload malicious code, viruses, or harmful content</li>
                <li>Impersonate any person or entity</li>
                <li>Violate the rights of other users or third parties</li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                5. Intellectual Property Rights
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                5.1 WealthPort Ownership
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                The Service, including all software, designs, text, graphics,
                interfaces, and the selection and arrangement thereof, is owned
                by WealthPort and is protected by copyright, trademark, and
                other intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                5.2 Your Data
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You retain all rights to the portfolio data, notes, and content
                you upload to the Service ("Your Data"). By using the Service,
                you grant WealthPort a limited license to use Your Data solely
                to provide and improve the Service.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                You represent and warrant that you have all necessary rights to
                Your Data and that Your Data does not violate any third-party
                rights.
              </p>
            </section>

            {/* Third-Party Services */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                6. Third-Party Services
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                WealthPort integrates with the following third-party services:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                6.1 SnapTrade (Brokerage Connections)
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                WealthPort uses SnapTrade to enable secure, read-only connections
                to your brokerage accounts. When you connect an account through
                SnapTrade:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>SnapTrade acts as a third-party aggregation service</li>
                <li>
                  You are subject to SnapTrade's Terms of Service and Privacy
                  Policy
                </li>
                <li>
                  WealthPort receives only read-only access to your holdings,
                  balances, and transaction history — no trading authority is
                  granted
                </li>
                <li>You own your account data and may revoke access at any time</li>
                <li>You can disconnect SnapTrade access at any time within the Service</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Disclaimer:</strong> WealthPort is not responsible for
                SnapTrade's security, data practices, or service availability.
                Any issues with SnapTrade connectivity should be directed to
                SnapTrade's support.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                6.2 Plaid (Brokerage Connections)
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                WealthPort may also use Plaid Inc. to enable secure connections
                to certain brokerage or bank accounts. When you connect an
                account through Plaid:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Plaid acts as a third-party service provider</li>
                <li>
                  You are subject to Plaid's Terms of Service and Privacy Policy
                </li>
                <li>
                  Plaid may access your brokerage login credentials and account
                  data
                </li>
                <li>We receive only the data you authorize through Plaid</li>
                <li>You can disconnect Plaid access at any time</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Disclaimer:</strong> WealthPort is not responsible for
                Plaid's security, data practices, or service availability. Any
                issues with Plaid connectivity should be directed to Plaid's
                support.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                6.3 Yahoo Finance (Market Data)
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We use Yahoo Finance APIs (via the yfinance library) to retrieve
                stock prices and market data:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Market data may be delayed by 15-20 minutes or more</li>
                <li>
                  Data accuracy is not guaranteed and depends on Yahoo's service
                </li>
                <li>
                  Yahoo may track which tickers you query through our service
                </li>
                <li>
                  This service is provided for informational purposes only
                </li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Disclaimer:</strong> WealthPort is not affiliated with
                Yahoo or its parent companies. Market data is provided "as is"
                without warranties. Do not rely solely on this data for trading
                decisions.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                6.4 StockTwits (Market Intelligence)
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                WealthPort may retrieve publicly available posts and sentiment
                data from StockTwits to assist you in researching securities:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>
                  StockTwits content is user-generated and reflects the opinions
                  of third parties, not WealthPort
                </li>
                <li>
                  This content is provided solely for informational research
                  purposes and does not constitute investment advice
                </li>
                <li>
                  You are subject to StockTwits' Terms of Service when content
                  sourced from their platform is displayed
                </li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Disclaimer:</strong> WealthPort is not affiliated with
                StockTwits. Third-party opinions displayed through the Service
                do not represent WealthPort's views.
              </p>
            </section>

            {/* Intel and Alerts */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                7. Research Intel and Price Alerts
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                WealthPort provides tools for capturing and organizing your own
                investment research ("Intel") and for setting price-based
                notifications ("Alerts"). The following terms govern their use:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>
                  <strong>User-Initiated Only:</strong> All Intel entries are
                  created solely by you. WealthPort never generates, pushes, or
                  auto-populates investment recommendations without your explicit
                  action. No suggestion or alert will ever appear unless you
                  have first created the underlying Intel.
                </li>
                <li>
                  <strong>Not Investment Advice:</strong> Intel and Alerts are
                  organizational and notification tools only. They reflect
                  information you have chosen to track and do not constitute
                  investment advice or a recommendation to buy or sell any
                  security.
                </li>
                <li>
                  <strong>Price Alerts:</strong> Alerts are triggered solely by
                  price thresholds you configure. Receiving an alert does not
                  mean WealthPort is recommending any action. All trading
                  decisions remain entirely your own.
                </li>
                <li>
                  <strong>Alert Notifications:</strong> When a price threshold
                  you set is reached, WealthPort may send you an email
                  notification. These emails are sent only in response to
                  conditions you explicitly defined and do not constitute
                  unsolicited financial recommendations.
                </li>
                <li>
                  <strong>No Automated Trading:</strong> WealthPort does not
                  execute, automate, or recommend any trades based on Intel or
                  Alerts. You remain in full control of all trading activity in
                  your brokerage accounts.
                </li>
              </ul>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                8. Limitation of Liability
              </h2>
              <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 mb-4">
                <p className="text-gray-900 dark:text-gray-100 font-semibold">
                  IMPORTANT LEGAL NOTICE
                </p>
              </div>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  <strong>No Liability for Trading Losses:</strong> WealthPort
                  is not liable for any investment losses, trading decisions, or
                  financial outcomes resulting from your use of the Service.
                </li>
                <li>
                  <strong>Service Provided "AS IS":</strong> The Service is
                  provided "as is" and "as available" without warranties of any
                  kind, either express or implied, including but not limited to
                  warranties of merchantability, fitness for a particular
                  purpose, or non-infringement.
                </li>
                <li>
                  <strong>No Consequential Damages:</strong> WealthPort shall
                  not be liable for any indirect, incidental, special,
                  consequential, or punitive damages, including lost profits,
                  lost revenue, lost data, or loss of goodwill.
                </li>
                <li>
                  <strong>Maximum Liability:</strong> Our total liability to you
                  for any claims arising from your use of the Service shall not
                  exceed the amount you paid to WealthPort in the 12 months
                  preceding the claim, or $100, whichever is greater.
                </li>
                <li>
                  <strong>Data Loss:</strong> We are not liable for any data
                  loss, corruption, or deletion. You are responsible for
                  maintaining backups of Your Data.
                </li>
              </ul>
            </section>

            {/* Indemnification */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                9. Indemnification
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                You agree to indemnify, defend, and hold harmless WealthPort,
                its officers, directors, employees, and agents from and against
                any claims, liabilities, damages, losses, and expenses,
                including reasonable attorneys' fees, arising out of or in any
                way connected with: (a) your access to or use of the Service;
                (b) Your Data; (c) your violation of these Terms; or (d) your
                violation of any third-party rights.
              </p>
            </section>

            {/* Termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                10. Termination
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                10.1 By You
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You may terminate your account at any time by contacting us or
                using the account deletion feature within the Service.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                10.2 By WealthPort
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We reserve the right to suspend or terminate your access to the
                Service at any time, with or without cause, with or without
                notice, including if:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>You violate these Terms</li>
                <li>Your account has been inactive for an extended period</li>
                <li>We are required to do so by law</li>
                <li>We discontinue the Service</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                10.3 Effect of Termination
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                Upon termination, your right to use the Service will immediately
                cease. You may request an export of Your Data within 30 days of
                termination, after which Your Data may be deleted in accordance
                with our Privacy Policy.
              </p>
            </section>

            {/* Dispute Resolution */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                11. Dispute Resolution and Arbitration
              </h2>
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-4 mb-4">
                <p className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
                  BINDING ARBITRATION NOTICE
                </p>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  Please read this section carefully. It affects your legal
                  rights.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                11.1 Informal Resolution
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Before filing a claim, you agree to try to resolve the dispute
                informally by contacting us at legal@wealthport.ai. We will
                attempt to resolve the dispute informally within 60 days.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                11.2 Binding Arbitration
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If we cannot resolve the dispute informally, you agree that any
                dispute, claim, or controversy arising out of or relating to
                these Terms or the Service shall be resolved by binding
                arbitration administered by the American Arbitration Association
                (AAA) under its Commercial Arbitration Rules.
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  Arbitration will be conducted in Delaware, United States
                </li>
                <li>The arbitrator's decision will be final and binding</li>
                <li>Each party will bear its own costs and attorneys' fees</li>
                <li>
                  <strong>
                    You waive your right to a jury trial and to participate in a
                    class action lawsuit
                  </strong>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                11.3 Exceptions
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                Either party may seek injunctive relief in court for
                intellectual property infringement or unauthorized access to the
                Service.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                12. Governing Law
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                These Terms shall be governed by and construed in accordance
                with the laws of the State of Delaware, United States, without
                regard to its conflict of law provisions. Any legal action
                arising out of these Terms (to the extent not subject to
                arbitration) shall be brought exclusively in the state or
                federal courts located in Delaware.
              </p>
            </section>

            {/* Export Control */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                13. Export Control and Sanctions
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                You may not use the Service if you are located in, or a resident
                of, any country subject to U.S. economic sanctions or export
                controls. You represent that you are not on any U.S. government
                list of prohibited or restricted parties.
              </p>
            </section>

            {/* Severability */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                14. Severability and Waiver
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If any provision of these Terms is found to be invalid or
                unenforceable, the remaining provisions will remain in full
                force and effect. Our failure to enforce any right or provision
                of these Terms will not be deemed a waiver of such right or
                provision.
              </p>
            </section>

            {/* Entire Agreement */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                15. Entire Agreement
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                These Terms, together with our Privacy Policy, constitute the
                entire agreement between you and WealthPort regarding the
                Service and supersede all prior agreements and understandings.
              </p>
            </section>

            {/* Changes to Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                16. Changes to Terms
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                We reserve the right to modify these Terms at any time. If we
                make material changes, we will notify you by posting the updated
                Terms on this page and updating the "Last Updated" date. Your
                continued use of the Service after changes become effective
                constitutes acceptance of the modified Terms.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                17. Contact Information
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If you have questions about these Terms, please contact us:
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>WealthPort</strong>
                  <br />
                  Email: legal@wealthport.ai
                  <br />
                  For support: support@wealthport.ai
                </p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                18. Your Acknowledgment
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                BY USING WEALTHPORT, YOU ACKNOWLEDGE THAT YOU HAVE READ,
                UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                You further acknowledge that:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>WealthPort is not providing financial advice</li>
                <li>
                  You are solely responsible for your investment decisions
                </li>
                <li>Trading involves substantial risk of loss</li>
                <li>Data and calculations may contain errors</li>
                <li>
                  You agree to binding arbitration and waive class action rights
                </li>
              </ul>
            </section>
          </div>

          {/* Action Buttons */}
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate("/")}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Back to Home
            </button>
            <button
              onClick={() => navigate("/login")}
              className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
